import { useTranscription, useUserIdentity } from '@src/stores-v2/StoreContext';
import { parseClipThemesJson } from '@src/utility/story';
import { useMemo } from 'react';

import { useTopicParagraphMapping } from '../TranscriptSpeakersTab/useTopicParagraphMapping';
import { topicSpansFromMapping } from './storyTopicSpans';

import type { TopicSpans } from './storyTopicSpans';
import type { Story } from '@src/types.ts/story';

/**
 * Where the story's topics are spoken, for the Add Clips picker's topic filter.
 *
 * Rides the *same* topic→paragraph mapping the Transcript & Speakers tab
 * computes: cached on the story, persisted to DatoCMS, and only re-derived when
 * the topics or the transcript actually change (`TopicMappingOrchestrator`). So
 * a story that has been through that tab costs nothing here, and one that has
 * not pays the one tagging call once, for every surface.
 *
 * `enabled` is the Reel Editor tab being open rather than the picker: the
 * mapping is a click away by then, and resolving it in advance means the filter
 * is there when the picker opens instead of appearing a beat later.
 */
export function useStoryTopicSpans(input: {
  story: Story | null | undefined;
  enabled: boolean;
}): TopicSpans | undefined {
  const { story, enabled } = input;
  const transcription = useTranscription();
  const userIdentity = useUserIdentity();

  const topics = useMemo(
    () => (enabled ? parseClipThemesJson(story?.clipThemesJson) : []),
    [enabled, story?.clipThemesJson],
  );

  // The story's full transcript, not the clip's — a topic's extent is a stretch
  // of the story, and the clips are matched against it in source time.
  const originalElements = transcription.originalTranscription?.elements;

  const { mapping } = useTopicParagraphMapping({
    enabled,
    story: story ?? null,
    topics,
    originalElements,
    isTranscriptionReady: transcription.isTranscriptionReady,
    userIdentity: userIdentity.currentIdentity ?? undefined,
  });

  return useMemo(
    () => topicSpansFromMapping(mapping, originalElements),
    [mapping, originalElements],
  );
}
